import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { useAuth, apiErr } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BG = "https://images.unsplash.com/photo-1704455306251-b4634215d98f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenue 👋");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen w-full lg:grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={BG} alt="Cabinet dentaire" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#043d2e]/95 via-[#065f46]/70 to-[#065f46]/30" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <span className="rounded-2xl bg-white p-2"><img src="/logo-idagray-mark.png" alt="Logo IDA GRAY" className="h-14 w-auto object-contain" /></span>
            <span className="leading-none"><span className="block font-display text-[22px] font-semibold tracking-[0.08em]">IDA GRAY</span><span className="mt-1 block text-[11px] font-semibold uppercase tracking-[0.22em] text-[#E5CFA4]">Gestion du cabinet</span></span>
          </div>
          <div>
            <h1 className="font-display text-4xl font-black leading-tight">
              Comprenez votre trésorerie en 30 secondes.
            </h1>
            <p className="mt-4 max-w-md text-white/80">
              Aujourd'hui → Fin du mois → Mois prochain → 12 mois. Un copilote financier simple pour votre cabinet
              dentaire.
            </p>
          </div>
          <p className="text-sm text-white/50">© {new Date().getFullYear()} — IDA GRAY · Finances, ressources humaines, parc & maintenance</p>
        </div>
      </div>

      <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <img src="/logo-idagray-mark.png" alt="Logo IDA GRAY" className="h-14 w-auto object-contain" />
            <span className="leading-none"><span className="block font-display text-[21px] font-semibold tracking-[0.08em] text-[#0F2E25]">IDA GRAY</span><span className="mt-1 block text-[10.5px] font-semibold uppercase tracking-[0.22em] text-slate-500">Gestion du cabinet</span></span>
          </div>
          <h2 className="font-display text-3xl font-bold">
            Bon retour
          </h2>
          <p className="mt-2 text-sm text-gray-500">Connectez-vous pour piloter votre cabinet.</p>

          <button
            data-testid="google-login-btn"
            onClick={google}
            className="mt-8 flex w-full items-center justify-center gap-3 rounded-full border border-gray-200 bg-white py-3 font-semibold text-gray-700 transition-colors hover:bg-gray-50 active:scale-95"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
            Continuer avec Google
          </button>

          <div className="my-6 flex items-center gap-4 text-xs text-gray-400">
            <div className="h-px flex-1 bg-gray-200" /> OU <div className="h-px flex-1 bg-gray-200" />
          </div>

          <form onSubmit={submit} className="space-y-4" autoComplete="off">
            <input type="text" name="username" autoComplete="username" tabIndex={-1} aria-hidden="true" className="hidden" readOnly />
            <input type="password" name="password" autoComplete="current-password" tabIndex={-1} aria-hidden="true" className="hidden" readOnly />
            <div>
              <Label>Email</Label>
              <Input data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 rounded-xl" placeholder="vous@cabinet.fr" name="ig-login-id" autoComplete="off" readOnly={locked} onFocus={() => setLocked(false)} />
            </div>
            <div>
              <Label>Mot de passe</Label>
              <Input data-testid="password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 rounded-xl" placeholder="••••••••" name="ig-login-secret" autoComplete="new-password" readOnly={locked} onFocus={() => setLocked(false)} />
            </div>
            <Button
              data-testid="submit-auth-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#065F46] py-6 text-base font-semibold hover:bg-[#047857]"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                <span className="flex items-center gap-2">Se connecter <ArrowRight className="h-4 w-4" /></span>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500" data-testid="invite-only-note">
            Accès réservé à l'équipe du cabinet. Pour obtenir un compte, contactez l'administrateur.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
